/**
 * EnumTag - 动态枚举标签组件
 *
 * 根据枚举分类编码和值自动显示带颜色的标签。
 */

import React from 'react';
import { Tag, Spin } from 'antd';
import type { TagProps } from 'antd';
import { useEnumOptions } from '../hooks/useEnumOptions';

interface EnumTagProps extends Omit<TagProps, 'color'> {
  /** 枚举分类编码，如 'CASE_STATUS', 'EXPENSE_TYPE' */
  categoryCode: string;
  /** 枚举值编码 */
  value?: string;
  /** 当找不到对应标签时显示的文本 */
  fallbackText?: string;
}

export function EnumTag({
  categoryCode,
  value,
  fallbackText,
  style,
  className,
  ...restProps
}: EnumTagProps) {
  const { options, loading } = useEnumOptions(categoryCode);

  // 如果没有值，不显示
  if (!value) {
    return null;
  }

  // 正在加载且尚无缓存数据时显示加载状态
  if (loading && options.length === 0) {
    return <Spin size="small" />;
  }

  const option = options.find(o => o.value === value);
  const label = option?.label;
  const color = option?.color || 'default';
  const description = option?.description;

  // 显示标签（找不到匹配项时用 fallbackText 或原始 value）
  return (
    <Tag
      color={color}
      style={style}
      className={className}
      title={description || label || value}
      {...restProps}
    >
      {label || fallbackText || value}
    </Tag>
  );
}

/**
 * EnumTagList - 多个枚举标签列表组件
 */
interface EnumTagListProps {
  categoryCode: string;
  values: string[];
  maxCount?: number;
  style?: React.CSSProperties;
  className?: string;
}

export function EnumTagList({
  categoryCode,
  values,
  maxCount = 3,
  style,
  className,
}: EnumTagListProps) {
  const { options, loading } = useEnumOptions(categoryCode);

  if (loading && options.length === 0) {
    return <Spin size="small" />;
  }

  // 获取每个值的标签信息
  const tags = values.map(value => {
    const option = options.find(o => o.value === value);
    return {
      value,
      label: option?.label || value,
      color: option?.color,
    };
  });

  // 显示数量限制
  const visibleTags = tags.slice(0, maxCount);
  const hiddenCount = tags.length - maxCount;

  return (
    <span style={style} className={className}>
      {visibleTags.map(tag => (
        <Tag key={tag.value} color={tag.color}>
          {tag.label}
        </Tag>
      ))}
      {hiddenCount > 0 && (
        <Tag>+{hiddenCount}</Tag>
      )}
    </span>
  );
}

export default EnumTag;